import React, { useState, useEffect } from 'react'
import {
	Card,
	Form,
	Input,
	Button,
	message,
	Spin,
	Typography,
	Divider,
	Alert,
	Row,
	Col,
	Tooltip,
	Tag,
} from 'antd'
import { httpGet, httpPut } from '../../common/http.service'
import { MobileOutlined, EditOutlined, AndroidOutlined, AppleOutlined } from '@ant-design/icons'

const { Text, Paragraph } = Typography

interface AppVersionConfig {
	androidVersion: string
	iosVersion: string
	androidPlayStoreUrl: string
	iosAppStoreUrl: string
}

const AppVersionTab: React.FC = () => {
	const [form] = Form.useForm()
	const [loading, setLoading] = useState(false)
	const [exists, setExists] = useState(false)
	const [editing, setEditing] = useState(false)
	const [currentValues, setCurrentValues] = useState<AppVersionConfig | null>(null)

	useEffect(() => {
		loadAppVersion()
	}, [])

	const loadAppVersion = async () => {
		try {
			setLoading(true)
			const response: any = await httpGet('/api/v1/admin/app-version')
			if (response?.data) {
				form.setFieldsValue(response.data)
				setCurrentValues(response.data)
				setExists(true)
			}
		} catch (error) {
			console.error('Failed to load app version configuration:', error)
		} finally {
			setLoading(false)
		}
	}

	const handleSubmit = async (values: AppVersionConfig) => {
		try {
			setLoading(true)
			await httpPut('/api/v1/admin/app-version', values)
			message.success('App version configuration saved successfully')
			setCurrentValues(values)
			setExists(true)
			setEditing(false)
			loadAppVersion()
		} catch (error: any) {
			message.error(
				error?.response?.data?.message || 'Failed to save app version configuration',
			)
		} finally {
			setLoading(false)
		}
	}

	return (
		<div>
			<Card
				title={
					<span>
						<MobileOutlined style={{ marginRight: 8 }} />
						App Version Management
					</span>
				}
				bordered={false}
				style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
				extra={
					!editing && (
						<Tooltip title='Edit the minimum required app version'>
							<Button
								type='primary'
								icon={<EditOutlined />}
								onClick={() => {
									if (currentValues) {
										form.setFieldsValue(currentValues)
									}
									setEditing(true)
								}}
							>
								{exists ? 'Edit' : 'Set Version'}
							</Button>
						</Tooltip>
					)
				}
			>
				<Spin spinning={loading}>
					<Paragraph>
						Configure the minimum required app version for Android and iOS clients.
						Users running older versions will be prompted to update.
					</Paragraph>
					<Divider />

					<Alert
						message='Minimum Required Version'
						description='Set the minimum app version required for check-in/check-out. Versions below this will trigger a forced update prompt on the mobile app.'
						type='info'
						showIcon
						style={{ marginBottom: 24 }}
					/>

					{!editing && exists && currentValues ? (
						<div>
							<Row gutter={[24, 16]}>
								<Col span={12}>
									<Card
										size='small'
										style={{
											backgroundColor: '#f6ffed',
											border: '1px solid #b7eb8f',
										}}
									>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: 8,
											}}
										>
											<AndroidOutlined
												style={{ fontSize: 20, color: '#52c41a' }}
											/>
											<div>
												<Text type='secondary' style={{ fontSize: 12 }}>
													Android Min Version
												</Text>
												<div>
													<Tag color='green' style={{ fontSize: 14 }}>
														v{currentValues.androidVersion}
													</Tag>
												</div>
											</div>
										</div>
									</Card>
								</Col>
								<Col span={12}>
									<Card
										size='small'
										style={{
											backgroundColor: '#f0f5ff',
											border: '1px solid #adc6ff',
										}}
									>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: 8,
											}}
										>
											<AppleOutlined
												style={{ fontSize: 20, color: '#2f54eb' }}
											/>
											<div>
												<Text type='secondary' style={{ fontSize: 12 }}>
													iOS Min Version
												</Text>
												<div>
													<Tag color='blue' style={{ fontSize: 14 }}>
														v{currentValues.iosVersion}
													</Tag>
												</div>
											</div>
										</div>
									</Card>
								</Col>
								<Col span={12}>
									<Text strong>Android Play Store URL</Text>
									<div style={{ marginTop: 4 }}>
										<Text
											copyable
											ellipsis
											style={{ fontSize: 12, color: '#52c41a' }}
										>
											{currentValues.androidPlayStoreUrl}
										</Text>
									</div>
								</Col>
								<Col span={12}>
									<Text strong>iOS App Store URL</Text>
									<div style={{ marginTop: 4 }}>
										<Text
											copyable
											ellipsis
											style={{ fontSize: 12, color: '#2f54eb' }}
										>
											{currentValues.iosAppStoreUrl}
										</Text>
									</div>
								</Col>
							</Row>
						</div>
					) : (
						<Form form={form} layout='vertical' onFinish={handleSubmit}>
							<Row gutter={16}>
								<Col span={12}>
									<Form.Item
										name='androidVersion'
										label={
											<span>
												<AndroidOutlined style={{ marginRight: 4 }} />
												Android Min Version
											</span>
										}
										rules={[
											{
												required: true,
												message: 'Please enter the Android version',
											},
											{
												pattern: /^\d+\.\d+\.\d+$/,
												message: 'Use semantic versioning (e.g., 2.0.0)',
											},
										]}
										tooltip='Minimum required version in semantic versioning format (e.g., 2.0.0)'
									>
										<Input placeholder='e.g., 2.0.0' />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										name='iosVersion'
										label={
											<span>
												<AppleOutlined style={{ marginRight: 4 }} />
												iOS Min Version
											</span>
										}
										rules={[
											{
												required: true,
												message: 'Please enter the iOS version',
											},
											{
												pattern: /^\d+\.\d+\.\d+$/,
												message: 'Use semantic versioning (e.g., 2.0.0)',
											},
										]}
										tooltip='Minimum required version in semantic versioning format (e.g., 2.0.0)'
									>
										<Input placeholder='e.g., 2.0.0' />
									</Form.Item>
								</Col>
							</Row>
							<Form.Item
								name='androidPlayStoreUrl'
								label='Android Play Store URL'
								rules={[
									{ required: true, message: 'Please enter the Play Store URL' },
									{ type: 'url', message: 'Please enter a valid URL' },
								]}
							>
								<Input placeholder='https://play.google.com/store/apps/details?id=...' />
							</Form.Item>
							<Form.Item
								name='iosAppStoreUrl'
								label='iOS App Store URL'
								rules={[
									{ required: true, message: 'Please enter the App Store URL' },
									{ type: 'url', message: 'Please enter a valid URL' },
								]}
							>
								<Input placeholder='https://apps.apple.com/...' />
							</Form.Item>

							<div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
								<Button type='primary' htmlType='submit' loading={loading}>
									Save
								</Button>
								{exists && (
									<Button
										onClick={() => {
											setEditing(false)
											if (currentValues) {
												form.setFieldsValue(currentValues)
											}
										}}
									>
										Cancel
									</Button>
								)}
							</div>
						</Form>
					)}
				</Spin>
			</Card>
		</div>
	)
}

export default AppVersionTab
